import React, { useState, useEffect } from 'react';
import { Plus, ExternalLink, Trash2, Edit2, Palette, FolderOpen, BookOpen, Sparkles, Loader } from 'lucide-react';
import { Button, Card, Modal } from '../../components/UI';
import { materialsApi } from '../../api';
import './Materials.css';

const CATEGORIES = [
  { value: 'all', label: 'All', icon: FolderOpen },
  { value: 'design', label: 'Design', icon: Palette },
  { value: 'project', label: 'Projects', icon: FolderOpen },
  { value: 'article', label: 'Articles', icon: BookOpen },
];

const Materials = () => {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingMaterial, setEditingMaterial] = useState(null);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    url: '',
    category: 'design',
    tags: '',
    image_url: ''
  });
  const [analyzing, setAnalyzing] = useState(false);
  const [analyzeError, setAnalyzeError] = useState('');

  useEffect(() => {
    fetchMaterials();
  }, [selectedCategory]);

  const fetchMaterials = async () => {
    try {
      setLoading(true);
      const response = await materialsApi.getAll(selectedCategory);
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingMaterial) {
        await materialsApi.update(editingMaterial.id, formData);
      } else {
        await materialsApi.create(formData);
      }
      setIsModalOpen(false);
      setEditingMaterial(null);
      resetForm();
      fetchMaterials();
    } catch (error) {
      console.error('Error saving material:', error);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this item?')) {
      try {
        await materialsApi.delete(id);
        fetchMaterials();
      } catch (error) {
        console.error('Error deleting material:', error);
      }
    }
  };

  const handleEdit = (material) => {
    setEditingMaterial(material);
    setFormData({
      title: material.title,
      description: material.description || '',
      url: material.url || '',
      category: material.category,
      tags: material.tags || '',
      image_url: material.image_url || ''
    });
    setIsModalOpen(true);
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      url: '',
      category: 'design',
      tags: '',
      image_url: ''
    });
    setAnalyzeError('');
  };

  const handleAnalyzeUrl = async () => {
    if (!formData.url) {
      setAnalyzeError('Please enter a URL first');
      return;
    }
    
    try {
      setAnalyzing(true);
      setAnalyzeError('');
      const response = await materialsApi.analyzeUrl(formData.url);
      const { title, description, image_url } = response.data;
      
      setFormData(prev => ({
        ...prev,
        title: prev.title || title,
        description: description,
        image_url: image_url || prev.image_url,
        category: 'article'
      }));
    } catch (error) {
      console.error('Error analyzing URL:', error);
      setAnalyzeError(error.response?.data?.error || 'Failed to analyze URL');
    } finally {
      setAnalyzing(false);
    }
  };

  const openNewModal = () => {
    setEditingMaterial(null);
    resetForm();
    setIsModalOpen(true);
  };

  const getCategoryIcon = (category) => {
    const cat = CATEGORIES.find(c => c.value === category);
    return cat ? cat.icon : FolderOpen;
  };

  return (
    <div className="materials-page">
      <div className="page-header">
        <div>
          <h1>Material Library</h1>
          <p className="page-subtitle">Curated collection of inspirations and resources</p>
        </div>
        <Button icon={Plus} onClick={openNewModal}>
          Add Material
        </Button>
      </div>

      <div className="materials-filters">
        {CATEGORIES.map(({ value, label, icon: Icon }) => (
          <button
            key={value}
            className={`filter-btn ${selectedCategory === value ? 'active' : ''}`}
            onClick={() => setSelectedCategory(value)}
          >
            <Icon size={16} />
            <span>{label}</span>
          </button>
        ))}
      </div>

      {loading ? (
        <div className="loading-state">
          <div className="loading-spinner" />
          <p>Loading materials...</p>
        </div>
      ) : materials.length === 0 ? (
        <div className="empty-state">
          <FolderOpen size={48} strokeWidth={1} />
          <h3>No materials yet</h3>
          <p>Start building your collection of inspirations</p>
          <Button icon={Plus} onClick={openNewModal}>
            Add Your First Material
          </Button>
        </div>
      ) : (
        <div className="materials-grid">
          {materials.map((material) => {
            const CategoryIcon = getCategoryIcon(material.category);
            return (
              <Card key={material.id} className="material-card" hover>
                {material.image_url && (
                  <div className="material-image">
                    <img src={material.image_url} alt={material.title} />
                  </div>
                )}
                <div className="material-content">
                  <div className="material-category">
                    <CategoryIcon size={14} />
                    <span>{material.category}</span>
                  </div>
                  <h3 className="material-title">{material.title}</h3>
                  {material.description && (
                    <p className="material-description">{material.description}</p>
                  )}
                  {material.tags && (
                    <div className="material-tags">
                      {material.tags.split(',').map((tag, index) => (
                        <span key={index} className="tag">{tag.trim()}</span>
                      ))}
                    </div>
                  )}
                  <div className="material-actions">
                    {material.url && (
                      <a 
                        href={material.url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="action-btn"
                      >
                        <ExternalLink size={16} />
                      </a>
                    )}
                    <button className="action-btn" onClick={() => handleEdit(material)}>
                      <Edit2 size={16} />
                    </button>
                    <button className="action-btn danger" onClick={() => handleDelete(material.id)}>
                      <Trash2 size={16} />
                    </button>
                  </div>
                </div>
              </Card>
            );
          })}
        </div>
      )}

      <Modal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        title={editingMaterial ? 'Edit Material' : 'Add New Material'}
        size="medium"
      >
        <form onSubmit={handleSubmit}>
          <div className="form-group">
            <label className="form-label">Title *</label>
            <input
              type="text"
              className="form-input"
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              placeholder="Enter title"
              required
            />
          </div>

          <div className="form-group">
            <label className="form-label">Category *</label>
            <select
              className="form-select"
              value={formData.category}
              onChange={(e) => setFormData({ ...formData, category: e.target.value })}
            >
              <option value="design">Design</option>
              <option value="project">Project</option>
              <option value="article">Article</option>
            </select>
          </div>

          <div className="form-group">
            <label className="form-label">Description</label>
            <textarea
              className="form-textarea"
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Brief description..."
              rows={3}
            />
          </div>

          <div className="form-group">
            <label className="form-label">URL</label>
            <div className="url-input-group">
              <input
                type="url"
                className="form-input"
                value={formData.url}
                onChange={(e) => setFormData({ ...formData, url: e.target.value })}
                placeholder="https://..."
              />
              <button
                type="button"
                className={`ai-analyze-btn ${analyzing ? 'analyzing' : ''}`}
                onClick={handleAnalyzeUrl}
                disabled={analyzing || !formData.url}
                title="AI Analyze URL"
              >
                {analyzing ? <Loader size={16} className="spin" /> : <Sparkles size={16} />}
                <span>{analyzing ? 'Analyzing...' : 'AI Analyze'}</span>
              </button>
            </div>
            {analyzeError && <p className="analyze-error">{analyzeError}</p>}
          </div>

          <div className="form-group">
            <label className="form-label">Image URL</label>
            <input
              type="url"
              className="form-input"
              value={formData.image_url}
              onChange={(e) => setFormData({ ...formData, image_url: e.target.value })}
              placeholder="https://..."
            />
          </div>

          <div className="form-group">
            <label className="form-label">Tags</label>
            <input
              type="text"
              className="form-input"
              value={formData.tags}
              onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
              placeholder="Separate tags with commas"
            />
          </div>

          <div className="form-actions">
            <Button variant="ghost" onClick={() => setIsModalOpen(false)}>
              Cancel
            </Button>
            <Button type="submit" variant="gold">
              {editingMaterial ? 'Update' : 'Add Material'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
};

export default Materials;
